/**
 * <s-portrait-customizer> — step-by-step portrait customizer.
 *
 * Markup contract (all inside the element):
 *   script[data-product-json]       product JSON (options, variants)
 *   form                            native multipart cart form (no-JS fallback)
 *   [data-step]                     one panel per step, shown one at a time
 *   input[data-option-index]        option radios; earlier steps constrain later ones
 *   input[data-photo-input]         pet photo file input, [data-photo-later] checkbox
 *   [data-gift-toggle] [data-gift-message]  gift message, optional fee variant on [data-gift]
 *   input[data-addon]               extra add-on variants (checkbox)
 *   input[name="extra_prints"]      0–3 extra prints of [data-prints] variant
 *   [data-summary] [data-summary-total]     live order summary
 *
 * The main portrait is added with a multipart request so the photo is stored
 * on the order line; extras follow as their own lines, then the cart opens.
 */
const MAX_PHOTO_BYTES = 20 * 1024 * 1024;

class SPortraitCustomizer extends HTMLElement {
  #ac;
  #product;
  #step = 0;
  #steps = [];
  #form;
  #previewUrl = '';

  connectedCallback() {
    const json = this.querySelector('[data-product-json]');
    this.#form = this.querySelector('form');
    if (!json || !this.#form) return;
    this.#product = JSON.parse(json.textContent);
    this.#steps = [...this.querySelectorAll('[data-step]')];

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('change', (e) => this.#onChange(e), opts);
    this.addEventListener('input', (e) => this.#onInput(e), opts);
    this.addEventListener('click', (e) => this.#onClick(e), opts);
    this.#form.addEventListener('submit', (e) => this.#onSubmit(e), opts);

    this.#showStep(0, false);
    this.#refreshOptions();
    this.#updateSummary();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    if (this.#previewUrl) URL.revokeObjectURL(this.#previewUrl);
  }

  /* ---------- money ---------- */

  #money(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const value = (Number(cents) || 0) / 100;
    const fmt = (decimals, thousands, decimal) => {
      const [whole, frac] = value.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${grouped}${decimal}${frac}` : grouped;
    };
    const text = format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return fmt(0, ',', '.');
      if (key === 'amount_with_comma_separator') return fmt(2, '.', ',');
      if (key === 'amount_no_decimals_with_comma_separator') return fmt(0, '.', ',');
      return fmt(2, ',', '.');
    });
    return text.replace(/<[^>]*>/g, '');
  }

  /* ---------- variants ---------- */

  #selected() {
    return this.#product.options.map((_, i) => {
      const input = this.querySelector(`input[data-option-index="${i}"]:checked`);
      return input ? input.value : null;
    });
  }

  #variantFor(values) {
    return this.#product.variants.find((v) => v.options.every((o, i) => o === values[i])) || null;
  }

  #variant() {
    return this.#variantFor(this.#selected());
  }

  /** Earlier choices decide which later values are offered; later invalid picks move to the first valid one. */
  #refreshOptions(changedIndex = -1) {
    const count = this.#product.options.length;
    const values = this.#selected();

    if (changedIndex >= 0 && !this.#variantFor(values)) {
      const prefix = values.slice(0, changedIndex + 1);
      const match =
        this.#product.variants.find((v) => v.available && prefix.every((p, i) => v.options[i] === p)) ||
        this.#product.variants.find((v) => prefix.every((p, i) => v.options[i] === p));
      if (match) {
        for (let i = changedIndex + 1; i < count; i++) {
          const input = [...this.querySelectorAll(`input[data-option-index="${i}"]`)].find((el) => el.value === match.options[i]);
          if (input) input.checked = true;
        }
      }
    }

    const current = this.#variant();
    const chosen = this.#selected();

    for (const input of this.querySelectorAll('input[data-option-index]')) {
      const index = Number(input.dataset.optionIndex);
      const earlier = chosen.slice(0, index);
      const compatible = this.#product.variants.some((v) => earlier.every((p, i) => v.options[i] === p) && v.options[index] === input.value);
      const label = input.closest('[data-option-card]');
      if (label) label.hidden = !compatible;

      const trial = chosen.slice();
      trial[index] = input.value;
      const candidate = this.#variantFor(trial);
      const diffEl = label?.querySelector('[data-price-diff]');
      if (diffEl) {
        let text = '';
        if (candidate && current && !input.checked) {
          const diff = candidate.price - current.price;
          if (diff > 0) text = `+${this.#money(diff)}`;
          else if (diff < 0) text = `−${this.#money(-diff)}`;
        }
        if (candidate && !candidate.available) text = this.dataset.unavailableLabel || '';
        diffEl.textContent = text;
      }
    }

    for (const out of this.querySelectorAll('[data-selected-value]')) {
      const index = Number(out.dataset.selectedValue);
      out.textContent = chosen[index] || '';
    }

    const idInput = this.#form.querySelector('input[name="id"]');
    if (current && idInput) idInput.value = current.id;

    const addButton = this.querySelector('[data-add-button]');
    if (addButton) {
      const available = Boolean(current?.available);
      addButton.disabled = !available;
      addButton.textContent = available ? this.dataset.addLabel : this.dataset.soldOutLabel;
    }

    for (const price of this.querySelectorAll('[data-live-price]')) {
      if (current) price.textContent = this.#money(current.price);
    }
    for (const compare of this.querySelectorAll('[data-live-compare]')) {
      const show = current && current.compare_at_price > current.price;
      compare.hidden = !show;
      if (show) compare.textContent = this.#money(current.compare_at_price);
    }

    if (changedIndex >= 0 && current?.featured_image?.src && this.dataset.gallery) {
      document.getElementById(this.dataset.gallery)?.select?.(current.featured_image.src);
    }
  }

  /* ---------- events ---------- */

  #onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-option-index]')) {
      this.#refreshOptions(Number(target.dataset.optionIndex));
    } else if (target.matches('[data-photo-input]')) {
      this.#onPhoto(target);
    } else if (target.matches('[data-photo-later]')) {
      if (target.checked) this.#photoError('');
    } else if (target.matches('[data-gift-toggle]')) {
      const fields = this.querySelector('[data-gift-fields]');
      if (fields) fields.hidden = !target.checked;
    } else if (target.matches('[data-addon]')) {
      const state = target.closest('[data-addon-card]')?.querySelector('[data-addon-state]');
      if (state) state.textContent = target.checked ? this.dataset.addedLabel : this.dataset.addLabelShort;
    }
    this.#updateSummary();
  }

  #onInput(event) {
    const target = event.target;
    if (!(target instanceof HTMLTextAreaElement) || !target.matches('[data-gift-message]')) return;
    const count = this.querySelector('[data-gift-count]');
    if (count) {
      count.textContent = (this.dataset.giftCountTemplate || '')
        .replace('%count%', String(target.value.length))
        .replace('%max%', String(target.maxLength));
    }
    const preview = this.querySelector('[data-gift-preview]');
    if (preview) preview.textContent = target.value.trim() || this.dataset.giftEmpty || '';
  }

  #onClick(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-step-next], [data-step-back]') : null;
    if (!button) return;
    event.preventDefault();
    if (button.matches('[data-step-back]')) {
      this.#showStep(this.#step - 1);
      return;
    }
    if (!this.#validateStep(this.#step)) return;
    this.#showStep(this.#step + 1);
  }

  /* ---------- steps ---------- */

  #showStep(index, moveFocus = true) {
    const next = Math.max(0, Math.min(index, this.#steps.length - 1));
    this.#step = next;
    this.#steps.forEach((panel, i) => {
      panel.hidden = i !== next;
    });
    const total = this.#steps.length;
    for (const el of this.querySelectorAll('[data-step-counter]')) {
      el.textContent = (this.dataset.stepTemplate || '')
        .replace('%current%', String(next + 1))
        .replace('%total%', String(total));
    }
    for (const bar of this.querySelectorAll('[data-step-progress]')) {
      bar.style.width = `${((next + 1) / total) * 100}%`;
    }
    const back = this.querySelector('[data-step-back]');
    if (back) back.hidden = next === 0;
    const nextButton = this.querySelector('[data-step-next]');
    if (nextButton) nextButton.hidden = next === total - 1;
    const finish = this.querySelector('[data-finish]');
    if (finish) finish.hidden = next !== total - 1;

    if (moveFocus) {
      const heading = this.#steps[next]?.querySelector('[data-step-heading]');
      heading?.focus({ preventScroll: true });
      this.#steps[next]?.scrollIntoView({ block: 'nearest', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  }

  #validateStep(index) {
    const panel = this.#steps[index];
    if (!panel?.querySelector('[data-photo-input]')) return true;
    return this.#photoReady();
  }

  /* ---------- photo ---------- */

  #photoReady() {
    const input = this.querySelector('[data-photo-input]');
    const later = this.querySelector('[data-photo-later]');
    if (!input) return true;
    if (later?.checked || input.files?.length) {
      this.#photoError('');
      return true;
    }
    this.#photoError(this.dataset.photoRequired);
    return false;
  }

  #onPhoto(input) {
    const file = input.files?.[0];
    const preview = this.querySelector('[data-photo-preview]');
    const empty = this.querySelector('[data-photo-empty]');
    if (!file) return;

    let error = '';
    if (!file.type.startsWith('image/') && !/\.(heic|heif)$/i.test(file.name)) error = this.dataset.photoType;
    else if (file.size > MAX_PHOTO_BYTES) error = this.dataset.photoSize;

    if (error) {
      input.value = '';
      this.#photoError(error);
      return;
    }

    this.#photoError('');
    const later = this.querySelector('[data-photo-later]');
    if (later) later.checked = false;
    if (this.#previewUrl) URL.revokeObjectURL(this.#previewUrl);
    this.#previewUrl = URL.createObjectURL(file);
    const img = preview?.querySelector('img');
    if (img) img.src = this.#previewUrl;
    const name = preview?.querySelector('[data-photo-name]');
    if (name) name.textContent = file.name;
    if (preview) preview.hidden = false;
    if (empty) empty.hidden = true;
  }

  #photoError(message) {
    const el = this.querySelector('[data-photo-error]');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }

  /* ---------- summary ---------- */

  #extras() {
    const lines = [];
    const gift = this.querySelector('[data-gift]');
    const giftToggle = this.querySelector('[data-gift-toggle]');
    const giftMessage = this.querySelector('[data-gift-message]');
    if (gift?.dataset.feeVariant && giftToggle?.checked && giftMessage?.value.trim()) {
      lines.push({ id: gift.dataset.feeVariant, quantity: 1, price: Number(gift.dataset.feePrice), title: this.dataset.giftLabel });
    }
    for (const addon of this.querySelectorAll('input[data-addon]:checked')) {
      lines.push({ id: addon.value, quantity: 1, price: Number(addon.dataset.price), title: addon.dataset.title });
    }
    const prints = this.querySelector('[data-prints]');
    const printCount = Number(this.querySelector('input[name="extra_prints"]:checked')?.value || 0);
    if (prints && printCount > 0) {
      lines.push({
        id: prints.dataset.variant,
        quantity: printCount,
        price: Number(prints.dataset.price) * printCount,
        title: (this.dataset.printsTemplate || '').replace('%count%', String(printCount)),
        print: true,
      });
    }
    return lines;
  }

  #updateSummary() {
    const list = this.querySelector('[data-summary]');
    const totalEl = this.querySelector('[data-summary-total]');
    const variant = this.#variant();
    if (!list || !totalEl) return;

    const rows = [];
    if (variant) {
      const title = variant.title === 'Default Title' ? this.#product.title : `${this.#product.title} · ${variant.title}`;
      rows.push([title, variant.price]);
    }
    for (const line of this.#extras()) rows.push([line.title, line.price]);

    list.replaceChildren(
      ...rows.map(([label, cents]) => {
        const row = document.createElement('div');
        row.className = 'flex items-baseline justify-between gap-4';
        const dt = document.createElement('dt');
        dt.className = 'text-on-surface-secondary';
        dt.textContent = label;
        const dd = document.createElement('dd');
        dd.className = 'shrink-0 font-semibold text-on-surface';
        dd.textContent = this.#money(cents);
        row.append(dt, dd);
        return row;
      })
    );
    totalEl.textContent = this.#money(rows.reduce((sum, [, cents]) => sum + cents, 0));
  }

  /* ---------- add to cart ---------- */

  async #onSubmit(event) {
    event.preventDefault();
    if (this.getAttribute('aria-busy') === 'true') return;
    if (!this.#photoReady()) {
      const uploadIndex = this.#steps.findIndex((s) => s.querySelector('[data-photo-input]'));
      if (uploadIndex >= 0) this.#showStep(uploadIndex);
      return;
    }

    const variant = this.#variant();
    if (!variant?.available) return;

    const button = this.querySelector('[data-add-button]');
    const error = this.querySelector('[data-add-error]');
    this.setAttribute('aria-busy', 'true');
    button?.setAttribute('aria-disabled', 'true');
    if (error) error.hidden = true;

    try {
      const main = new FormData(this.#form);
      main.delete('extra_prints');
      const later = this.querySelector('[data-photo-later]');
      if (later?.checked) main.set(`properties[${this.dataset.photoProperty}]`, this.dataset.photoLaterValue);
      const giftToggle = this.querySelector('[data-gift-toggle]');
      if (!giftToggle?.checked) main.delete(`properties[${this.dataset.giftLabel}]`);

      await this.#post(main);

      const extras = this.#extras();
      if (extras.length) {
        const forValue = variant.title === 'Default Title' ? this.#product.title : `${this.#product.title} · ${variant.title}`;
        const items = extras.map((line) => {
          const properties = { [this.dataset.forLabel]: forValue };
          if (line.print) properties[this.dataset.printsProperty] = this.dataset.printsValue;
          return { id: Number(line.id), quantity: line.print ? line.quantity : 1, properties };
        });
        await this.#post(JSON.stringify({ items }), true);
      }

      window.location.href = this.dataset.cartUrl;
    } catch (err) {
      if (error) {
        error.textContent = err instanceof Error && err.message ? err.message : this.dataset.errorMessage;
        error.hidden = false;
      }
      this.removeAttribute('aria-busy');
      button?.removeAttribute('aria-disabled');
    }
  }

  async #post(body, json = false) {
    const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
      method: 'POST',
      headers: json ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
      body,
    });
    if (!response.ok) {
      let message = this.dataset.errorMessage;
      try {
        const data = await response.json();
        message = data.description || data.message || message;
      } catch {
        /* keep default message */
      }
      throw new Error(message);
    }
  }
}

if (!customElements.get('s-portrait-customizer')) customElements.define('s-portrait-customizer', SPortraitCustomizer);
